import React from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Container,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { PaymentResponse, PaymentStatusResponse } from "../controller";

interface CreditCardResultProps {
  variant: "credit-card";
  onBack: () => void;
  paymentStatus?: PaymentStatusResponse;
  paymentStatusPolling?: boolean;
  paymentStatusError?: string;
}

interface PixResultProps {
  variant: "pix";
  paymentResponse: PaymentResponse;
  onBack: () => void;
  paymentStatus?: PaymentStatusResponse;
  paymentStatusPolling?: boolean;
  paymentStatusError?: string;
}

interface BankSlipResultProps {
  variant: "bank-slip";
  paymentResponse: PaymentResponse;
  onBack: () => void;
}

interface ErrorResultProps {
  variant: "error";
  message?: string;
  onBack: () => void;
}

type PaymentResultProps =
  | CreditCardResultProps
  | PixResultProps
  | BankSlipResultProps
  | ErrorResultProps;

const PAYMENT_STATUS = {
  AWAITING_PAYMENT: 4,
  APPROVED: 6,
  CANCELED: 7,
  CONTESTATION: 24,
  MONITORING: 87,
  FAILED: 89,
} as const;

type PaymentStatusVariant = "credit-card" | "pix" | "bank-slip";

const STATUS_LABELS: Record<number, string> = {
  [PAYMENT_STATUS.AWAITING_PAYMENT]: "Aguardando pagamento",
  [PAYMENT_STATUS.APPROVED]: "Pagamento confirmado",
  [PAYMENT_STATUS.CANCELED]: "O pagamento foi cancelado",
  [PAYMENT_STATUS.CONTESTATION]: "Pagamento em contestação",
  [PAYMENT_STATUS.MONITORING]: "Pagamento em monitoramento",
  [PAYMENT_STATUS.FAILED]: "Falha no pagamento",
};

const getStatusMessage = ({
  variant,
  statusId,
  expired,
}: {
  variant: PaymentStatusVariant;
  statusId?: number;
  expired?: boolean;
}): string | undefined => {
  if (expired) {
    return "Gere um novo pagamento para continuar.";
  }

  switch (statusId) {
    case PAYMENT_STATUS.AWAITING_PAYMENT:
      switch (variant) {
        case "credit-card":
          return "Estamos aguardando a confirmação do operador do cartão.";
        case "bank-slip":
          return "Estamos aguardando a confirmação do pagamento do boleto.";
        default:
          return undefined;
      }
    case PAYMENT_STATUS.APPROVED:
      return "Pagamento confirmado! Em breve seus produtos serão liberados.";
    case PAYMENT_STATUS.CANCELED:
      return "Gere um novo pagamento para continuar.";
    case PAYMENT_STATUS.CONTESTATION:
      return "O pagamento está em contestação junto ao provedor. Aguarde o retorno.";
    case PAYMENT_STATUS.MONITORING:
      return "O pagamento está em monitoramento pelo provedor. Continue acompanhando.";
    case PAYMENT_STATUS.FAILED:
      switch (variant) {
        case "credit-card":
          return "Não foi possível concluir o pagamento no cartão. Tente novamente ou escolha outro método.";
        case "pix":
          return "Não foi possível concluir o pagamento via PIX. Tente novamente ou escolha outra forma de pagamento.";
        default:
          return "Não foi possível concluir o pagamento. Tente novamente ou escolha outra forma de pagamento.";
      }
    default:
      return "Status do pagamento atualizado. Continue acompanhando pelo provedor.";
  }
};

const getStatusLabel = ({
  statusId,
  expired,
  fallback,
}: {
  statusId?: number;
  expired?: boolean;
  fallback?: string;
}): string => {
  if (expired) return "Pagamento expirado";
  if (statusId !== undefined && STATUS_LABELS[statusId]) {
    return STATUS_LABELS[statusId];
  }
  if (fallback) return fallback;
  return "Status atualizado";
};

const SuccessContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Container
    maxWidth="lg"
    sx={{
      py: { xs: 5, md: 7 },
      px: { xs: 2, md: 3 },
      display: "flex",
      justifyContent: "center",
    }}
  >
    <Box
      width="100%"
      maxWidth={720}
      bgcolor="background.paper"
      display="flex"
      flexDirection="column"
      gap={{ xs: 2.5, md: 3 }}
      p={{ xs: 2.5, md: 4 }}
      borderRadius={4}
      border={(theme) => `1px solid ${theme.palette.grey[300]}`}
    >
      {children}
    </Box>
  </Container>
);

const getPaymentStatusColor = ({
  statusId,
  expired,
}: {
  statusId?: number;
  expired?: boolean;
}): string => {
  if (expired) return "error.main";

  switch (statusId) {
    case PAYMENT_STATUS.APPROVED:
      return "success.main";
    case PAYMENT_STATUS.CANCELED:
    case PAYMENT_STATUS.FAILED:
      return "error.main";
    case PAYMENT_STATUS.CONTESTATION:
    case PAYMENT_STATUS.MONITORING:
      return "warning.main";
    default:
      return "primary.main";
  }
};

const renderPaymentStatusInfo = ({
  statusId,
  statusName,
  expired,
  paymentStatusPolling,
  paymentStatusError,
}: {
  statusId?: number;
  statusName?: string;
  expired?: boolean;
  paymentStatusPolling?: boolean;
  paymentStatusError?: string;
}): React.ReactNode => {
  if (
    !statusName &&
    statusId === undefined &&
    !paymentStatusPolling &&
    !paymentStatusError
  ) {
    return null;
  }

  const statusColor = getPaymentStatusColor({ statusId, expired });
  const showSpinner =
    ((statusId ?? PAYMENT_STATUS.AWAITING_PAYMENT) ===
      PAYMENT_STATUS.AWAITING_PAYMENT &&
      !expired) ||
    Boolean(paymentStatusPolling);

  return (
    <Stack
      mt={2}
      spacing={1}
      alignItems="center"
      justifyContent="center"
      sx={{ textAlign: "center", width: "100%" }}
    >
      <Typography variant="overline" color="text.secondary">
        Status
      </Typography>
      <Typography variant="h6" sx={{ color: statusColor }}>
        {statusName ?? "Status atualizado"}
      </Typography>
      {showSpinner && <CircularProgress size={20} />}
      {paymentStatusPolling && (
        <Typography variant="body2" color="text.secondary">
          Atualizando status do pagamento…
        </Typography>
      )}
      {paymentStatusError && (
        <Typography variant="body2" color="error">
          {paymentStatusError}
        </Typography>
      )}
    </Stack>
  );
};

const renderCreditCard = ({
  onBack,
  paymentStatus,
  paymentStatusPolling,
  paymentStatusError,
}: CreditCardResultProps) => {
  const statusId =
    paymentStatus?.paymentStatusId ?? PAYMENT_STATUS.AWAITING_PAYMENT;
  const expired = paymentStatus?.expired ?? false;
  const statusName = getStatusLabel({
    statusId,
    expired,
    fallback: paymentStatus?.paymentStatusName,
  });
  const statusMessage = getStatusMessage({
    variant: "credit-card",
    statusId,
    expired,
  });

  const paymentStatusInfo = renderPaymentStatusInfo({
    statusId,
    statusName,
    expired,
    paymentStatusPolling,
    paymentStatusError,
  });

  return (
    <SuccessContainer>
      <Stack spacing={{ xs: 2, md: 2.5 }} alignItems="center">
        <Typography color="primary" variant="h4" fontWeight={600} align="center">
          Pagamento com Cartão de Crédito
        </Typography>
        {paymentStatusInfo}
        {statusMessage && (
          <Typography
            color="text.primary"
            variant="body1"
            align="center"
            sx={{ maxWidth: 520 }}
          >
            {statusMessage}
          </Typography>
        )}
        <Box pt={{ xs: 1.5, md: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={onBack}
          >
            Voltar
          </Button>
        </Box>
      </Stack>
    </SuccessContainer>
  );
};

const PixResultContent: React.FC<PixResultProps> = ({
  paymentResponse,
  paymentStatus,
  paymentStatusPolling,
  paymentStatusError,
  onBack,
}) => {
  const [copySnackbarOpen, setCopySnackbarOpen] = React.useState(false);

  const dueDate = paymentResponse.dueDate
    ? paymentResponse.dueDate instanceof Date
      ? paymentResponse.dueDate
      : new Date(paymentResponse.dueDate)
    : undefined;

  const formattedDueDate = dueDate && !Number.isNaN(dueDate.getTime()) ? dueDate : undefined;
  const rawStatusId =
    paymentStatus?.paymentStatusId ?? paymentResponse.paymentStatusId;
  const isExpired =
    paymentStatus?.expired ?? paymentResponse.expired ?? false;
  const resolvedStatusId =
    rawStatusId ?? PAYMENT_STATUS.AWAITING_PAYMENT;
  const statusName = getStatusLabel({
    statusId: resolvedStatusId,
    expired: isExpired,
    fallback:
      paymentStatus?.paymentStatusName ??
      paymentResponse.paymentStatusName,
  });
  const shouldShowPaymentDetails =
    !isExpired && resolvedStatusId === PAYMENT_STATUS.AWAITING_PAYMENT;
  const statusMessage = getStatusMessage({
    variant: "pix",
    statusId: resolvedStatusId,
    expired: isExpired,
  });

  const paymentStatusInfo = renderPaymentStatusInfo({
    statusId: resolvedStatusId,
    statusName,
    expired: isExpired,
    paymentStatusPolling,
    paymentStatusError,
  });

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setCopySnackbarOpen(false);
  };

  const handleCopyPixCode = () => {
    const pixCode = paymentResponse.paymentPixCopyPaste;
    if (!pixCode) {
      return;
    }

    const copyWithFallback = () => {
      if (typeof document === "undefined") {
        return false;
      }

      try {
        const textarea = document.createElement("textarea");
        textarea.value = pixCode;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (successful) {
          setCopySnackbarOpen(true);
        }
        return successful;
      } catch (error) {
        console.error("Erro ao copiar código PIX", error);
        return false;
      }
    };

    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(pixCode)
        .then(() => {
          setCopySnackbarOpen(true);
        })
        .catch(() => {
          copyWithFallback();
        });
      return;
    }

    copyWithFallback();
  };

  return (
    <SuccessContainer>
      <Stack spacing={{ xs: 2, md: 2.5 }} alignItems="center" sx={{ width: "100%" }}>
        <Typography color="primary" variant="h4" fontWeight={600} align="center">
          Pagamento via PIX
        </Typography>
        {paymentStatusInfo}
        {!shouldShowPaymentDetails && statusMessage && (
          <Typography
            color="text.primary"
            variant="body1"
            align="center"
            sx={{ maxWidth: 560 }}
          >
            {statusMessage}
          </Typography>
        )}
        {shouldShowPaymentDetails && (
          <Stack spacing={{ xs: 1.75, md: 2 }} alignItems="center" sx={{ textAlign: "center", width: "100%" }}>
            {formattedDueDate && (
              <Stack spacing={0.5} alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>
                  Pague até {formattedDueDate.toLocaleDateString()} {formattedDueDate.toLocaleTimeString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Após esse horário a compra será cancelada automaticamente.
                </Typography>
              </Stack>
            )}
            <Typography variant="h6" fontWeight={600}>
              Escaneie o QR Code ou copie o código PIX para concluir o pagamento
            </Typography>
            {paymentResponse.paymentQrCode && (
              <Box
                component="embed"
                src={paymentResponse.paymentQrCode}
                sx={{
                  maxWidth: 280,
                  maxHeight: 280,
                  width: "100%",
                  height: "auto",
                  borderRadius: 3,
                }}
              />
            )}
            {paymentResponse.paymentUrl && (
              <Typography variant="body2" color="text.secondary">
                Se não conseguir visualizar o QR Code,{" "}
                <a href={paymentResponse.paymentUrl}>abra o link do pagamento</a>.
              </Typography>
            )}
            {paymentResponse.paymentPixCopyPaste && (
              <Stack spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                  PIX copia e cola
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ wordBreak: "break-all", fontFamily: "monospace", px: { xs: 1, md: 2 } }}
                >
                  {paymentResponse.paymentPixCopyPaste}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
        <Box pt={{ xs: 2, md: 2.5 }} display={'flex'} gap={1.5} flexDirection={{ xs: 'column', sm: 'row' }} alignItems="center">
          {(paymentResponse.paymentPixCopyPaste && shouldShowPaymentDetails) && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCopyPixCode}
            >
              <ContentCopyIcon fontSize="small" sx={{ color: "white" }} />
              Copiar código PIX
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            onClick={onBack}
          >
            Voltar
          </Button>
        </Box>
      </Stack>
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: "100%" }}>
          Código PIX copiado com sucesso!
        </Alert>
      </Snackbar>
    </SuccessContainer>
  );
};

const BankSlipResult: React.FC<BankSlipResultProps> = ({ paymentResponse, onBack }) => {
  const raw = (paymentResponse as any).rawTransaction;
  const linhaDigitavel = (paymentResponse as any).boletoLinhaDigitavel
    ?? raw?.payment?.linha_digitavel
    ?? null;
  const [copySnackbarOpen, setCopySnackbarOpen] = React.useState(false);

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setCopySnackbarOpen(false);
  };

  const handleCopyLinhaDigitavel = () => {
    if (!linhaDigitavel) {
      return;
    }

    const copyWithFallback = () => {
      if (typeof document === "undefined") {
        return false;
      }

      try {
        const textarea = document.createElement("textarea");
        textarea.value = linhaDigitavel;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (successful) {
          setCopySnackbarOpen(true);
        }
        return successful;
      } catch (error) {
        console.error("Erro ao copiar linha digitável do boleto", error);
        return false;
      }
    };

    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(linhaDigitavel)
        .then(() => {
          setCopySnackbarOpen(true);
        })
        .catch(() => {
          copyWithFallback();
        });
      return;
    }

    copyWithFallback();
  };

  return (
    <SuccessContainer>
      <Stack spacing={{ xs: 2, md: 2.5 }} alignItems="center">
        <Typography color="primary" variant="h4" fontWeight={600} align="center">
          Pagamento via Boleto Bancário
        </Typography>

        <Typography color="text.primary" variant="body1" align="center" sx={{ maxWidth: 520 }}>
          Utilize o boleto abaixo para finalizar o pagamento.
        </Typography>

        {paymentResponse.paymentUrl && (
          <ButtonBase
            onClick={() => window.open(paymentResponse.paymentUrl!, "_blank")}
            sx={{
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.primary.main}`,
              padding: 1.5,
              px: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              bgcolor: (theme) => theme.palette.primary.main + "10",
              transition: "transform 120ms ease, box-shadow 120ms ease",
              "&:hover": {
                transform: "translateY(-1px)",
                bgcolor: (theme) => theme.palette.primary.dark + "20",
              },
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <ReceiptLongIcon sx={{ color: "primary.main" }} />
              <Typography color="primary" variant="subtitle1" fontWeight={600}>
                Visualizar boleto
              </Typography>
            </Stack>
          </ButtonBase>
        )}

        {linhaDigitavel && (
          <Stack spacing={1.5} alignItems="center" sx={{ width: "100%", textAlign: "center" }}>
            <Typography color="text.secondary" variant="subtitle2">
              Código de barras do boleto
            </Typography>
            <Typography
              color="text.primary"
              variant="body2"
              sx={{ wordBreak: "break-all", fontFamily: "monospace", px: { xs: 1, md: 2 } }}
            >
              {linhaDigitavel}
            </Typography>
          </Stack>
        )}

        <Box pt={{ xs: 2, md: 2.5 }} display={'flex'} gap={1.5} flexDirection={{ xs: 'column', sm: 'row' }} alignItems="center">
          {linhaDigitavel && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCopyLinhaDigitavel}
            >
              <ContentCopyIcon fontSize="small" sx={{ color: "white" }} />
              Copiar código de barras
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            onClick={onBack}
          >
            Voltar
          </Button>
        </Box>
      </Stack>
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: "100%" }}>
          Código de barras copiado com sucesso!
        </Alert>
      </Snackbar>
    </SuccessContainer>
  );
};

const renderError = (message: string | undefined, onBack: () => void) => (
  <Container
    maxWidth="lg"
    sx={{
      py: { xs: 6, md: 8 },
      px: { xs: 2, md: 4 },
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <Box
      width="100%"
      bgcolor="background.paper"
      borderRadius={4}
      p={{ xs: 3, md: 5 }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      gap={3}
      border={(theme) => `1px solid ${theme.palette.grey[300]}`}
    >
      <ErrorOutlineIcon color="error" sx={{ fontSize: 56 }} />
      <Box display="flex" flexDirection="column" gap={1.5}>
        <Typography color="error" variant="h4">
          Erro ao processar pagamento
        </Typography>
        <Typography color="text.secondary" variant="body1">
          Ocorreu um erro ao processar seu pagamento. Por favor, tente novamente mais tarde.
        </Typography>
        {message && (
          <Typography color="text.primary" variant="body2">
            {message}
          </Typography>
        )}
        <Typography color="text.secondary" variant="body1">
          Caso o erro persista, entre em contato com o CATvante.
        </Typography>
      </Box>
      <Box pt={{ xs: 2, md: 2.5 }}>
        <Button
          variant="outlined"
          color="error"
          onClick={onBack}
        >
          Voltar
        </Button>
      </Box>
    </Box>
  </Container>
);

const PaymentResult: React.FC<PaymentResultProps> = (props) => {
  switch (props.variant) {
    case "credit-card":
      return renderCreditCard(props);
    case "pix":
      return <PixResultContent {...props} />;
    case "bank-slip":
      return <BankSlipResult {...props} />;
    case "error":
      return renderError(props.message, props.onBack);
    default:
      return null;
  }
};

export default PaymentResult;
